/**
 * "Remember me" session persistence.
 *
 * The app reads the token, refresh token and user from sessionStorage in ~16
 * places. Rather than teach every one of them about a second storage backend,
 * sessionStorage stays the single source of truth at runtime and this module
 * keeps a durable mirror in localStorage when the user asks to be remembered.
 *
 * On boot, `restoreRememberedSession()` copies the mirror back into
 * sessionStorage, so a remembered user returns to a signed-in app after closing
 * the browser while every existing reader keeps working unchanged.
 *
 * sessionStorage dies with the tab; localStorage does not. That difference IS
 * the feature, so a user who does not tick the box keeps the old behaviour.
 */

const MIRRORED_KEYS = ['token', 'refreshToken', 'user'] as const;
const REMEMBER_FLAG = 'auth_remember';
const REMEMBERED_EMAIL = 'auth_remembered_email';

/** Storage can throw in private-browsing modes; never let that break login. */
function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function isRemembered(): boolean {
  return safe(() => localStorage.getItem(REMEMBER_FLAG) === 'true', false);
}

/**
 * Called after a successful sign-in, once sessionStorage already holds the
 * session. When `remember` is false this clears any previous mirror, so
 * unticking the box on a later login actually forgets the old one.
 */
export function persistSession(remember: boolean, email?: string): void {
  safe(() => {
    if (!remember) {
      clearRememberedSession();
      return;
    }
    localStorage.setItem(REMEMBER_FLAG, 'true');
    if (email) localStorage.setItem(REMEMBERED_EMAIL, email);
    MIRRORED_KEYS.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (value) localStorage.setItem(key, value);
    });
  }, undefined);
}

/**
 * Refresh the mirror after a token rotation, so a remembered session does not
 * come back holding a stale access token. No-op when not remembered.
 */
export function syncRememberedSession(): void {
  if (!isRemembered()) return;
  safe(() => {
    MIRRORED_KEYS.forEach((key) => {
      const value = sessionStorage.getItem(key);
      if (value) localStorage.setItem(key, value);
      else localStorage.removeItem(key);
    });
  }, undefined);
}

/** Copy a remembered session back into sessionStorage. Call once at boot. */
export function restoreRememberedSession(): void {
  safe(() => {
    if (!isRemembered()) return;
    // An explicit sign-out must not be undone by the mirror.
    if (sessionStorage.getItem('logged_out') === 'true') return;
    if (sessionStorage.getItem('token')) return; // already signed in this tab
    if (!localStorage.getItem('token')) return;

    MIRRORED_KEYS.forEach((key) => {
      const value = localStorage.getItem(key);
      if (value) sessionStorage.setItem(key, value);
    });
  }, undefined);
}

/** Drop the durable copy. The remembered email is kept for convenience. */
export function clearRememberedSession(): void {
  safe(() => {
    localStorage.removeItem(REMEMBER_FLAG);
    MIRRORED_KEYS.forEach((key) => localStorage.removeItem(key));
  }, undefined);
}

export function getRememberedEmail(): string {
  return safe(() => localStorage.getItem(REMEMBERED_EMAIL) || '', '');
}

export function forgetRememberedEmail(): void {
  safe(() => localStorage.removeItem(REMEMBERED_EMAIL), undefined);
}
